import { EmptyObject } from '@crossbeam/types';

import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import {
  ACTIVE,
  ANNUAL_SUBSCRIPTION,
  ANNUAL_SUBSCRIPTION_V5,
  BILLING_PLAN_MONTHLY,
  BILLING_TIERS,
  CANCELLED,
  MONTHLY_SUBSCRIPTION,
  MONTHLY_SUBSCRIPTION_V5,
  PAST_DUE,
  UNPAID,
} from '@/constants/billing';
import { ordinal } from '@/date_time_utils';
import { captureException } from '@/errors';
import { useRootStore } from '@/stores/RootStore';
import { initStore } from '@/stores/store-utils';
import { BillingInfo, PaymentInfo, PortalInfo } from '@/types/billing';
import { createCrossbeamApiWithRetry, extractLocalDate } from '@/utils';

export const useBillingStore = defineStore('Billing', () => {
  const rootStore = useRootStore();
  const billingInfo = ref<BillingInfo | EmptyObject>({});
  const paymentInfo = ref<PaymentInfo | EmptyObject>({});
  const portalInfo = ref<PortalInfo | EmptyObject>({});

  const { initError, ready, readySync, running, refresh } = initStore(
    async (opts = { syncChargify: false }) => {
      const retryCrossbeamApi = createCrossbeamApiWithRetry() || crossbeamApi;

      /* Do not attempt to call billing endpoint in front of login */
      if (!rootStore.hasProfile && !(import.meta.env.MODE === 'test')) return;

      try {
        let resp;
        if (opts.syncChargify) {
          resp = await retryCrossbeamApi.POST('/v0.2/billing/sync', {
            body: { force: opts.force },
          });
          if (resp.data?.chargify_subscription)
            await refreshPortalAndPaymentInfo();
        } else resp = await retryCrossbeamApi.GET('/v0.2/billing');

        const { data } = resp;
        billingInfo.value = (data as BillingInfo) || {};
      } catch (err) {
        if (opts.syncChargify) {
          const { data } = await retryCrossbeamApi.GET('/v0.2/billing');
          billingInfo.value = (data as BillingInfo) || {};
          if (data?.chargify_subscription) await refreshPortalAndPaymentInfo();
          throw err; /* If the 'POST' call fails then retry with the 'GET' endpoint. Then throw, will be caught + stored in error */
        }
      }
    },
  );

  refresh({ useCache: true, syncChargify: true });

  const hasNoPaymentInfo = computed(
    () => !paymentInfo.value?.masked_card_number,
  );

  async function refreshPortalAndPaymentInfo() {
    try {
      const [portalResponse, paymentResponse] = await Promise.all([
        crossbeamApi.POST('/v0.2/billing/portal/sync'),
        crossbeamApi.GET('/v0.2/billing/payment'),
      ]);
      portalInfo.value = portalResponse.data || {};
      paymentInfo.value = paymentResponse.data || {};
    } catch (err) {
      captureException(err);
    }
  }

  function isSubscriptionHandleV5(handle: string) {
    return [ANNUAL_SUBSCRIPTION_V5, MONTHLY_SUBSCRIPTION_V5].includes(handle);
  }

  function isSubscriptionHandleLegacy(handle: string) {
    return [ANNUAL_SUBSCRIPTION, MONTHLY_SUBSCRIPTION].includes(handle);
  }

  const billingTier = computed(() => billingInfo.value.billing_tier);
  const billingVersion = computed(
    () => billingInfo.value.price_and_packaging_version,
  );
  const isFreeTier = computed(() => billingTier.value === BILLING_TIERS.FREE);
  const isConnectorTier = computed(
    () => billingTier.value === BILLING_TIERS.CONNECTOR,
  );
  const isSupernodeTier = computed(
    () => billingTier.value === BILLING_TIERS.SUPERNODE,
  );
  const isEnterpriseTier = computed(
    () => billingTier.value === BILLING_TIERS.ENTERPRISE,
  );
  const isSupernodeOrEnterpriseTier = computed(
    () => isSupernodeTier.value || isEnterpriseTier.value,
  );
  const customPopulationLimit = computed(
    () => billingInfo.value.core?.custom_population_limit ?? Infinity,
  );
  const onV5Subscription = computed(
    /* Self-serve connector orgs that upgraded after the launch of P&P v5
       in July 2025 are on the v5 pricing model */
    () => {
      const subscriptionHandle = chargifyData.value?.subscription_handle;
      return !!subscriptionHandle && isSubscriptionHandleV5(subscriptionHandle);
    },
  );
  const onLegacySubscription = computed(
    /* Self-serve connector orgs that upgraded before the launch of P&P v5
       in July 2025 are on the legacy pricing model */
    () => {
      const subscriptionHandle = chargifyData.value?.subscription_handle;
      return (
        !!subscriptionHandle && isSubscriptionHandleLegacy(subscriptionHandle)
      );
    },
  );
  const isLegacyConnectorPPv5 = computed(
    /* A Connector org is legacy if it's on legacy self-serve pricing
       or its billing term started before July 1, 2025 */
    () => billingInfo.value?.is_legacy_connector_ppv5 ?? false,
  );
  const partnerLimit = computed(
    () => billingInfo.value.core?.partner_limit ?? Infinity,
  );
  const recordExportLimit = computed(
    () => billingInfo.value.core?.record_export_limit,
  );
  const creditLimit = computed(() => billingInfo.value.core?.credit_limit);
  const creditsUsed = computed(() => billingInfo.value.core?.credits_used);
  const recordExportTermEndDate = computed(
    () => billingInfo.value.core?.record_export_term_end_date,
  );
  const reportExportLimit = computed(
    () => billingInfo.value.core?.report_export_limit ?? Infinity,
  );
  const hasRequestedFreeTrial = computed(
    () => billingInfo.value.core?.requested_free_trial,
  );
  const freeTrialEndDate = computed(() => billingInfo.value.core?.end_date);
  const isOnFreeTrial = computed(
    /* tier will always be connector if this is true */
    () => billingInfo.value.core?.on_free_trial,
  );
  const isNotPaying = computed(() => {
    return isOnFreeTrial.value || isFreeTier.value;
  });

  const hasSubscription = computed(() => !!chargifyData.value);
  const isOrderFormLegacy = computed(
    () => isLegacyConnectorPPv5.value && !hasSubscription.value,
  );
  const subscriptionStatus = computed(() => {
    const status = chargifyData.value?.subscription_status;
    if (status === ACTIVE && updateSummary.value?.cancellation_initiated)
      return CANCELLED;
    return status;
  });

  const isSubscriptionCancelled = computed(
    () => subscriptionStatus.value === CANCELLED,
  );
  const isSubscriptionPastDue = computed(
    () => subscriptionStatus.value === PAST_DUE,
  );
  const isSubscriptionUnpaid = computed(
    () => subscriptionStatus.value === UNPAID,
  );
  const hasPaymentIssue = computed(
    () =>
      hasNoPaymentInfo.value ||
      isSubscriptionPastDue.value ||
      isSubscriptionUnpaid.value,
  );
  const subscriptionCost = computed(
    () => chargifyData.value?.product_total_cents,
  );
  const componentCost = computed(
    () => chargifyData.value?.components_total_cents,
  );
  const grandTotal = computed(
    () => chargifyData.value?.subscription_total_cents,
  );
  const subscriptionErrorMessage = computed(
    () => chargifyData.value?.subscription_sync_error_message,
  );
  const subscriptionHandle = computed(() => {
    return chargifyData.value?.subscription_handle;
  });
  const subscriptionType = computed(() =>
    subscriptionHandle.value?.split('-').at(1),
  );
  const billingTierCreatedDate = computed(() => {
    const date = billingInfo.value?.tier_first_created_at;
    return date ? extractLocalDate(DateTime.fromISO(date)) : null;
  });
  const subscriptionDay = computed(() => {
    const date = chargifyData.value?.next_billing_date;
    return date ? ordinal(Number(DateTime.fromISO(date).toFormat('d'))) : null;
  });
  const subscriptionNextBillingDate = computed(() => {
    const date = chargifyData.value?.next_billing_date;
    return date ? extractLocalDate(DateTime.fromISO(date)) : null;
  });
  const subscriptionRenewalDate = computed(() => {
    const date = chargifyData.value?.next_renewal_date;
    return date ? extractLocalDate(DateTime.fromISO(date)) : null;
  });
  const lastPaidDate = computed(() => {
    const date = chargifyData.value?.last_paid_date;
    return date ? extractLocalDate(DateTime.fromISO(date)) : null;
  });
  const lastPaidAmount = computed(
    () => chargifyData.value?.last_paid_amount_cents,
  );
  const updateSummary = computed(() => chargifyData.value?.update_summary);
  const isMonthly = computed(
    () => subscriptionType.value === BILLING_PLAN_MONTHLY,
  );
  const billingPeriod = computed(() =>
    subscriptionType.value === BILLING_PLAN_MONTHLY ? 'month' : 'year',
  );
  const inRenewalPeriod = computed(() => chargifyData.value?.in_renewal_period);
  const purchaseSeatsEnabled = computed(
    () => isConnectorTier.value && hasSubscription.value,
  );
  const billingPortalURL = computed(() => portalInfo.value?.url);
  const creditCardType = computed(() => paymentInfo.value?.card_type);
  const creditCardLastFourDigits = computed(() =>
    paymentInfo.value?.masked_card_number?.split('-').at(-1),
  );
  const chargifyData = computed(() => billingInfo.value.chargify_subscription);
  const subtotal = computed(
    () => chargifyData.value?.subscription_subtotal_cents,
  );
  const discount = computed(
    () => chargifyData.value?.subscription_discount_cents,
  );
  const offlinePartnersQuota = computed(
    () => billingInfo.value.core?.offline_partners_quota,
  );
  const offlinePartnerOrgsUsed = computed(
    () => billingInfo.value.core?.offline_partner_orgs_used,
  );
  const openDataPartnersQuota = computed(
    () => billingInfo.value.core?.virtual_partners_quota,
  );
  const openDataPartnerOrgsUsed = computed(
    () => billingInfo.value.core?.virtual_partner_orgs_used,
  );
  const availableOpenDataQuota = computed(() => {
    if (
      openDataPartnersQuota.value === null ||
      openDataPartnersQuota.value === undefined ||
      openDataPartnerOrgsUsed.value === null ||
      openDataPartnerOrgsUsed.value === undefined
    ) {
      return 0;
    }
    return openDataPartnersQuota.value - openDataPartnerOrgsUsed.value;
  });
  const virtualPartnerAdditionsCap = computed(
    () => billingInfo.value.core?.virtual_partner_additions_cap ?? null,
  );
  const virtualPartnerAdditionsUsed = computed(
    () => billingInfo.value.core?.virtual_partner_additions_used ?? null,
  );
  // null means the lifetime additions cap is not enforced (org has no ODP quota),
  // so consumers should treat it as unlimited rather than zero remaining.
  const remainingLifetimeAdditions = computed(() => {
    const cap = virtualPartnerAdditionsCap.value;
    const used = virtualPartnerAdditionsUsed.value;
    if (cap === null || used === null) return null;
    return Math.max(cap - used, 0);
  });
  const isPartnerSponsor = computed(
    () => isSupernodeTier.value && billingInfo.value.core.is_partner_sponsor,
  );

  return {
    /* Common State */
    ready,
    readySync,
    running,
    error: initError,
    refreshBillingStore: refresh,

    /* Computeds and Functions */
    refreshPortalAndPaymentInfo,
    billingInfo,
    paymentInfo,
    portalInfo,
    billingTier,
    billingVersion,
    isFreeTier,
    isConnectorTier,
    isSupernodeTier,
    isEnterpriseTier,
    isSupernodeOrEnterpriseTier,
    customPopulationLimit,
    partnerLimit,
    recordExportLimit,
    creditLimit,
    creditsUsed,
    recordExportTermEndDate,
    reportExportLimit,
    hasSubscription,
    subscriptionStatus,
    isSubscriptionCancelled,
    isSubscriptionPastDue,
    isSubscriptionUnpaid,
    subscriptionCost,
    componentCost,
    grandTotal,
    subscriptionErrorMessage,
    subscriptionHandle,
    subscriptionType,
    billingPeriod,
    billingTierCreatedDate,
    hasRequestedFreeTrial,
    isOnFreeTrial,
    isNotPaying,
    freeTrialEndDate,
    subscriptionDay,
    subscriptionNextBillingDate,
    subscriptionRenewalDate,
    lastPaidDate,
    lastPaidAmount,
    updateSummary,
    isMonthly,
    inRenewalPeriod,
    purchaseSeatsEnabled,
    billingPortalURL,
    creditCardType,
    creditCardLastFourDigits,
    chargifyData,
    subtotal,
    discount,
    offlinePartnersQuota,
    offlinePartnerOrgsUsed,
    openDataPartnersQuota,
    openDataPartnerOrgsUsed,
    availableOpenDataQuota,
    virtualPartnerAdditionsCap,
    virtualPartnerAdditionsUsed,
    remainingLifetimeAdditions,
    isPartnerSponsor,
    onV5Subscription,
    onLegacySubscription,
    isLegacyConnectorPPv5,
    isOrderFormLegacy,
    hasNoPaymentInfo,
    hasPaymentIssue,
  };
});
